(function (root) {
  const KEYS = ['quilt', 'fitted', 'flat', 'standard', 'euro'];
  const MATERIALS = ['linen', 'cotton'];
  const purchaseFields = ['material', 'size', 'colour', 'quantity'];
  const same = (left, right) => String(left ?? '').trim().toLocaleLowerCase() === String(right ?? '').trim().toLocaleLowerCase();
  const slug = (value) => String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');

  function normaliseProduct(raw, key) {
    if (!raw || !Array.isArray(raw.variants)) return null;
    const options = raw.options || [];
    let sizeIndex = options.findIndex((name) => same(name, 'Size'));
    let colourIndex = options.findIndex((name) => same(name, 'Colour') || same(name, 'Color'));
    if (key === 'standard' || key === 'euro') sizeIndex = -1;
    if (colourIndex < 0) colourIndex = options.length > 1 ? (sizeIndex === 0 ? 1 : 0) : 0;
    const variants = raw.variants.map((variant) => ({
      id: Number(variant.id),
      size: sizeIndex < 0 ? (key === 'standard' ? 'Pair' : 'Single') : String(variant.options?.[sizeIndex] || ''),
      colour: String(variant.options?.[colourIndex] || ''),
      price: Number(variant.price),
      available: variant.available === true,
      image: variant.featured_image?.src || raw.image || null
    })).filter((variant) => Number.isSafeInteger(variant.id) && Number.isFinite(variant.price) && variant.colour);
    return { handle: raw.handle, title: raw.title, url: raw.url, image: raw.image, promoEligible: raw.promo_eligible === true, variants };
  }

  function createCatalog(rawProducts) {
    return Object.fromEntries(KEYS.map((key) => [key, Object.fromEntries(MATERIALS.map((material) => [material, normaliseProduct(rawProducts?.[key]?.[material], key)]))]));
  }

  function initialItem(key, catalog) {
    const material = catalog[key].linen ? 'linen' : 'cotton';
    return { material, size: key === 'standard' ? 'Pair' : key === 'euro' ? 'Single' : defaultSize(catalog, key, material), colour: '', quantity: 1, touched: false, confirmed: null };
  }

  function initialState(catalog) {
    return Object.fromEntries(KEYS.map((key) => [key, initialItem(key, catalog)]));
  }

  function productFor(catalog, key, material) {
    return catalog[key]?.[material] || null;
  }

  function sizes(catalog, key, material) {
    return [...new Set((productFor(catalog, key, material)?.variants || []).map((variant) => variant.size))];
  }

  function defaultSize(catalog, key, material) {
    return sizes(catalog, key, material).find((size) => same(size, 'Queen')) || '';
  }

  function colours(catalog, key, material) {
    return [...new Set((productFor(catalog, key, material)?.variants || []).map((variant) => variant.colour))];
  }

  function variantFor(catalog, key, item) {
    return productFor(catalog, key, item.material)?.variants.find((variant) => same(variant.size, item.size) && same(variant.colour, item.colour)) || null;
  }

  function statusFor(catalog, key, item) {
    if (!item.touched) return 'empty';
    if (!item.size || !item.colour) return 'incomplete';
    const variant = variantFor(catalog, key, item);
    return variant?.available ? 'complete' : 'unavailable';
  }

  function change(catalog, state, key, field, value) {
    const item = { ...state[key], touched: true };
    if (field === 'material' && value !== item.material) {
      item.material = value;
      if (!sizes(catalog, key, value).includes(item.size)) item.size = defaultSize(catalog, key, value);
      item.colour = '';
    } else {
      item[field] = value;
    }
    return { ...state, [key]: item };
  }

  function isConfirmed(catalog, key, item) {
    return !!item.confirmed && statusFor(catalog, key, item) === 'complete' && purchaseFields.every((field) => item[field] === item.confirmed[field]);
  }

  function confirmItem(catalog, state, key) {
    const item = state[key];
    if (statusFor(catalog, key, item) !== 'complete') return state;
    const confirmed = Object.fromEntries(purchaseFields.map((field) => [field, item[field]]));
    return { ...state, [key]: { ...item, confirmed: { ...confirmed, touched: true } } };
  }

  function discardChanges(catalog, state, key) {
    const confirmed = state[key].confirmed;
    return { ...state, [key]: confirmed ? { ...confirmed, confirmed: { ...confirmed } } : initialItem(key, catalog) };
  }

  function remove(catalog, state, key) {
    return { ...state, [key]: initialItem(key, catalog) };
  }

  function linesFor(catalog, state) {
    return KEYS.filter((key) => statusFor(catalog, key, state[key]) === 'complete').map((key) => ({
      key,
      ...state[key],
      product: productFor(catalog, key, state[key].material),
      variant: variantFor(catalog, key, state[key])
    }));
  }

  function confirmedState(catalog, state) {
    return Object.fromEntries(KEYS.map((key) => [key, state[key].confirmed || initialItem(key, catalog)]));
  }

  function previewState(catalog, state) {
    return Object.fromEntries(KEYS.map((key) => [key, statusFor(catalog, key, state[key]) === 'complete' ? state[key] : state[key].confirmed || initialItem(key, catalog)]));
  }

  function selectedLines(catalog, state) {
    return linesFor(catalog, confirmedState(catalog, state));
  }

  function previewLines(catalog, state) {
    return linesFor(catalog, previewState(catalog, state));
  }

  function subtotal(catalog, state) {
    return selectedLines(catalog, state).reduce((total, line) => total + line.variant.price * line.quantity, 0);
  }

  function promotionEstimate(catalog, state, promotion) {
    const lines = selectedLines(catalog, state);
    const original = subtotal(catalog, state);
    const tiers = promotion?.tiers || [];
    const valid = Array.isArray(tiers) && tiers.length >= 1 && tiers.length <= 4 && tiers.every((tier, index) =>
      Number.isInteger(tier.quantity) && tier.quantity >= 1 && Number.isInteger(tier.percent) && tier.percent >= 1 && tier.percent <= 100 &&
      (index === 0 || (tier.quantity > tiers[index - 1].quantity && tier.percent >= tiers[index - 1].percent))
    );
    const eligible = lines.filter((line) => line.product.promoEligible);
    const eligibleQuantity = eligible.reduce((count, line) => count + line.quantity, 0);
    const eligibleSubtotal = eligible.reduce((total, line) => total + line.variant.price * line.quantity, 0);
    const tier = valid ? [...tiers].reverse().find((entry) => eligibleQuantity >= entry.quantity) || null : null;
    const savings = tier ? Math.round(eligibleSubtotal * tier.percent / 100) : 0;
    return { valid, original, eligibleQuantity, eligibleSubtotal, tier, savings, estimatedTotal: original - savings };
  }

  function unfinished(catalog, state) {
    return KEYS.filter((key) => state[key].touched && !isConfirmed(catalog, key, state[key]));
  }

  function includedPillows(catalog, state, preview = false) {
    const quilt = preview ? previewState(catalog, state).quilt : confirmedState(catalog, state).quilt;
    return statusFor(catalog, 'quilt', quilt) === 'complete' && quilt.material === 'linen';
  }

  function previewLayers(catalog, state) {
    const chosen = previewState(catalog, state);
    const layers = [];
    const add = (slot, key, asset) => {
      const item = chosen[key];
      if (statusFor(catalog, key, item) === 'complete') layers.push({ slot, key, material: item.material, colour: item.colour, asset });
    };
    add('fitted', 'fitted', 'fitted');
    add('flat-under', 'flat', 'flat_under');
    if (statusFor(catalog, 'euro', chosen.euro) === 'complete') {
      if (chosen.euro.quantity === 2) add('euro-2', 'euro', 'euro_pair');
      add('euro-1', 'euro', 'euro_single');
    }
    const hasExtraStandard = statusFor(catalog, 'standard', chosen.standard) === 'complete';
    if (includedPillows(catalog, state, true) && hasExtraStandard) add('standard-2', 'quilt', 'rear');
    if (includedPillows(catalog, state, true) && !hasExtraStandard) add('standard-1', 'quilt', 'front');
    else add('standard-1', 'standard', 'front');
    add('quilt', 'quilt', 'quilt');
    return layers;
  }

  function cartItems(catalog, state) {
    return selectedLines(catalog, state).map((line) => ({ id: line.variant.id, quantity: line.quantity }));
  }

  function shareValue(catalog, state) {
    const preview = previewState(catalog, state);
    const parts = KEYS.flatMap((key) => {
      const item = preview[key];
      if (statusFor(catalog, key, item) !== 'complete') return [];
      const saved = state[key].confirmed;
      const savedValid = saved && statusFor(catalog, key, saved) === 'complete' && variantFor(catalog, key, saved);
      const describe = (name, choice) => `${name}.${slug(choice.material)}.${slug(choice.size)}.${slug(choice.colour)}.${choice.quantity}`;
      const matchesSaved = savedValid && purchaseFields.every((field) => item[field] === saved[field]);
      const current = `${describe(key, item)}${matchesSaved ? '.confirmed' : ''}`;
      return savedValid && !matchesSaved ? [current, describe(`${key}-saved`, saved)] : [current];
    });
    return parts.length ? `v2_${parts.join('_')}` : '';
  }

  function stateFromShareValue(catalog, value) {
    const restored = initialState(catalog);
    if (typeof value !== 'string' || value.length > 1024) return restored;
    if (value.startsWith('v2_')) {
      const seen = new Set();
      const savedParts = new Map();
      for (const part of value.slice(3).split('_')) {
        const match = /^(quilt|fitted|flat|standard|euro)(-saved)?\.(linen|cotton)\.([^.]+)\.([^.]+)\.([12])(\.confirmed)?$/.exec(part);
        if (!match) continue;
        const [, key, savedPart, material, size, colour, quantityText, confirmed] = match;
        const name = `${key}${savedPart || ''}`;
        if (seen.has(name) || (savedPart && confirmed)) continue;
        seen.add(name);
        const quantity = Number(quantityText);
        if (key !== 'euro' && quantity !== 1) continue;
        const variants = productFor(catalog, key, material)?.variants.filter((variant) => variant.available && slug(variant.size) === size && slug(variant.colour) === colour) || [];
        if (variants.length !== 1) continue;
        const item = { material, size: variants[0].size, colour: variants[0].colour, quantity, touched: true, confirmed: null };
        if (savedPart) savedParts.set(key, Object.fromEntries([...purchaseFields.map((field) => [field, item[field]]), ['touched', true]]));
        else {
          if (confirmed) item.confirmed = Object.fromEntries([...purchaseFields.map((field) => [field, item[field]]), ['touched', true]]);
          restored[key] = item;
        }
      }
      for (const [key, saved] of savedParts) {
        if (restored[key].touched && !restored[key].confirmed) restored[key].confirmed = saved;
      }
      return restored;
    }
    const parts = value.split('|');
    if (parts.shift() !== '1') return restored;
    const used = new Set();
    const matchVariant = (key, id) => {
      for (const material of MATERIALS) {
        const variant = productFor(catalog, key, material)?.variants.find((entry) => entry.id === id && entry.available);
        if (variant) return { material, variant };
      }
      return null;
    };
    for (const part of parts) {
      const match = /^(quilt|fitted|flat|standard|euro):([1-9]\d*):([12]):(0|[1-9]\d*):([012])$/.exec(part);
      if (!match || used.has(match[1])) continue;
      const [, key, currentId, quantityText, savedId, savedQuantityText] = match;
      used.add(key);
      const quantity = Number(quantityText);
      const savedQuantity = Number(savedQuantityText);
      if (key !== 'euro' && (quantity !== 1 || (savedId !== '0' && savedQuantity !== 1))) continue;
      const current = matchVariant(key, Number(currentId));
      if (!current) continue;
      const item = { material: current.material, size: current.variant.size, colour: current.variant.colour, quantity, touched: true, confirmed: null };
      if (savedId !== '0' && savedQuantity > 0) {
        const saved = matchVariant(key, Number(savedId));
        if (saved) item.confirmed = { material: saved.material, size: saved.variant.size, colour: saved.variant.colour, quantity: savedQuantity, touched: true };
      }
      restored[key] = item;
    }
    return restored;
  }

  function cartDeltaStatus(before, after, items) {
    const quantities = (cart) => {
      const counts = new Map();
      (cart.items || []).forEach((item) => {
        const id = Number(item.variant_id);
        counts.set(id, (counts.get(id) || 0) + Number(item.quantity));
      });
      return counts;
    };
    const old = quantities(before);
    const current = quantities(after);
    const differences = items.map((item) => (current.get(Number(item.id)) || 0) - (old.get(Number(item.id)) || 0));
    if (differences.every((difference, index) => difference >= items[index].quantity)) return 'complete';
    if (differences.some((difference) => difference > 0)) return 'partial';
    if (differences.every((difference) => difference === 0)) return 'none';
    return 'unknown';
  }

  const api = { KEYS, MATERIALS, createCatalog, initialState, productFor, sizes, colours, variantFor, statusFor, change, isConfirmed, confirmItem, discardChanges, remove, selectedLines, previewLines, subtotal, promotionEstimate, unfinished, includedPillows, previewLayers, cartItems, shareValue, stateFromShareValue, cartDeltaStatus, same };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ThlBedBuilderState = api;
})(typeof window === 'undefined' ? globalThis : window);
